import { useState, useEffect } from 'react';

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_KEY = process.env.REACT_APP_SUPABASE_PUBLISHABLE_KEY;

const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];

async function fetchSemesters(institutionId) {
  if (!institutionId) return [];

  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/semesters?institution_id=eq.${institutionId}&select=id,name,start_date,week_count,institution_id&order=start_date.desc`,
    {
      headers: {
        'apikey': SUPABASE_KEY,
        'Authorization': 'Bearer ' + SUPABASE_KEY
      }
    }
  );

  if (!response.ok) {
    const detail = await response.text();

    throw new Error(
      `Failed to load semesters (${response.status}): ${detail}`
    );
  }

  return response.json();
}

async function fetchCourseTitles(instructorId) {
  if (!instructorId) return [];

  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/courses?instructor_id=eq.${encodeURIComponent(instructorId)}&course_title_id=not.is.null&select=course_title_id,course_titles(id,name)&order=created_at.desc`,
    {
      headers: {
        'apikey': SUPABASE_KEY,
        'Authorization': 'Bearer ' + SUPABASE_KEY
      }
    }
  );

  if (!response.ok) {
    const detail = await response.text();

    throw new Error(
      `Failed to load your course list (${response.status}): ${detail}`
    );
  }

  const rows = await response.json();
  const byId = new Map();

  (Array.isArray(rows) ? rows : [])
    .forEach(row => {
      const title = row.course_titles;

      if (title?.id && title?.name) {
        byId.set(title.id, title);
      }
    });

  return [...byId.values()]
    .sort((a, b) =>
      String(a.name || '').localeCompare(
        String(b.name || ''),
        undefined,
        {
          sensitivity: 'base'
        }
      )
    );
}

async function fetchCourses(session) {
  const selectFields =
    'id,course_title_id,semester_id,name,class_days,' +
    'created_at,instructor_id,' +
    'course_titles(name),semesters(name,start_date)';

  const headers = {
    'apikey': SUPABASE_KEY,
    'Authorization': 'Bearer ' + SUPABASE_KEY
  };

  if (session.isSuperadmin) {
    const response = await fetch(
      `${SUPABASE_URL}/rest/v1/courses?select=${selectFields}&order=created_at.desc`,
      { headers }
    );

    if (!response.ok) {
      throw new Error(
        `Failed to load courses (${response.status})`
      );
    }

    return response.json();
  }

  const [
    ownedResponse,
    accessResponse
  ] = await Promise.all([
    fetch(
      `${SUPABASE_URL}/rest/v1/courses?instructor_id=eq.${session.instructorId}&select=${selectFields}&order=created_at.desc`,
      { headers }
    ),

    fetch(
      `${SUPABASE_URL}/rest/v1/course_access?instructor_id=eq.${session.instructorId}&select=courses(${selectFields})`,
      { headers }
    )
  ]);

  if (!ownedResponse.ok) {
    const detail =
      await ownedResponse.text();

    throw new Error(
      `Failed to load owned courses (${ownedResponse.status}): ${detail}`
    );
  }

  if (!accessResponse.ok) {
    const detail =
      await accessResponse.text();

    throw new Error(
      `Failed to load shared courses (${accessResponse.status}): ${detail}`
    );
  }

  const owned =
    await ownedResponse.json();

  const accessRows =
    await accessResponse.json();

  const sharedCourses =
    (Array.isArray(accessRows)
      ? accessRows
      : [])
      .map(row => row.courses)
      .filter(Boolean);

  const byId = new Map();

  [
    ...(Array.isArray(owned) ? owned : []),
    ...sharedCourses
  ].forEach(course => {
    byId.set(course.id, course);
  });

  return Array
    .from(byId.values())
    .sort((a, b) =>
      String(b.created_at || '')
        .localeCompare(
          String(a.created_at || '')
        )
    );
}

async function saveCourseTitle(name) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/course_titles`,
    {
      method: 'POST',
      headers: {
        'apikey': SUPABASE_KEY,
        'Authorization': 'Bearer ' + SUPABASE_KEY,
        'Content-Type': 'application/json',
        'Prefer': 'return=representation'
      },
      body: JSON.stringify({ name })
    }
  );

  if (!response.ok) {
    const detail = await response.text();
    throw new Error(
      `Failed to create course title (${response.status}): ${detail}`
    );
  }

  return response.json();
}

async function saveCourse(data) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/courses`,
    {
      method: 'POST',
      headers: {
        'apikey': SUPABASE_KEY,
        'Authorization': 'Bearer ' + SUPABASE_KEY,
        'Content-Type': 'application/json',
        'Prefer': 'return=representation'
      },
      body: JSON.stringify(data)
    }
  );

  if (!response.ok) {
    const detail = await response.text();
    throw new Error(
      `Failed to create course (${response.status}): ${detail}`
    );
  }

  const result = await response.json();

  if (!result?.[0]?.id) {
    throw new Error(
      'Course creation returned no course record.'
    );
  }

  return result;
}

async function saveWeeks(weeks) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/course_weeks`,
    {
      method: 'POST',
      headers: {
        'apikey': SUPABASE_KEY,
        'Authorization': 'Bearer ' + SUPABASE_KEY,
        'Content-Type': 'application/json',
        'Prefer': 'return=representation'
      },
      body: JSON.stringify(weeks)
    }
  );

  if (!response.ok) {
    const detail = await response.text();

    throw new Error(
      `Failed to create course weeks (${response.status}): ${detail}`
    );
  }

  const result = await response.json();

  if (!Array.isArray(result) || result.length === 0) {
    throw new Error(
      'No course weeks were created.'
    );
  }

  return result;
}

async function deleteCourse(id) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/courses?id=eq.${id}`,
    {
      method: 'DELETE',
      headers: {
        'apikey': SUPABASE_KEY,
        'Authorization': 'Bearer ' + SUPABASE_KEY
      }
    }
  );

  if (!response.ok) {
    const detail = await response.text();

    throw new Error(
      `Failed to delete course (${response.status}): ${detail}`
    );
  }
}

function generateWeeks(
  courseId,
  startDate,
  classDays,
  weekCount
) {
  const dayIndex = {
    Monday: 1,
    Tuesday: 2,
    Wednesday: 3,
    Thursday: 4,
    Friday: 5,
    Saturday: 6,
    Sunday: 0
  };

  const days = Array.isArray(classDays)
    ? classDays
    : [classDays];

  const count = Number(weekCount);

  if (
    !Number.isInteger(count) ||
    count < 1
  ) {
    throw new Error(
      'The selected semester does not have a valid week count. Please correct the semester in General Settings before creating this course.'
    );
  }

  const start = new Date(startDate);
  const weeks = [];

  days.forEach(day => {
    const target = dayIndex[day];

    let firstClass = new Date(start);

    while (
      firstClass.getDay() !== target
    ) {
      firstClass.setDate(
        firstClass.getDate() + 1
      );
    }

    for (let i = 0; i < count; i++) {
      const classDate =
        new Date(firstClass);

      classDate.setDate(
        firstClass.getDate() + i * 7
      );

      weeks.push({
        course_id: courseId,
        week_number: i + 1,
        class_date: classDate
          .toISOString()
          .split('T')[0],

        // New courses start with only Attendance active.
        // Assignment/activity systems are enabled explicitly later.
        cq_enabled: false,
        attendance_enabled: i < 16,
        pd_enabled: false
      });
    }
  });

  weeks.sort(
    (a, b) =>
      a.class_date.localeCompare(
        b.class_date
      )
  );

  return weeks;
}

export default function Courses({ session, onConfigure }) {
  const [semesters, setSemesters] = useState([]);
  const [titles, setTitles] = useState([]);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [semesterId, setSemesterId] = useState('');
  const [selectedTitleId, setSelectedTitleId] = useState('');
  const [manualName, setManualName] = useState('');
  const [addToTitles, setAddToTitles] = useState(false);
  const [classDays, setClassDays] = useState([]);

  useEffect(() => {
  setLoading(true);
  setError('');
  setSemesterId('');

  Promise.all([
    fetchSemesters(session.institutionId),
    fetchCourseTitles(session.instructorId),
    fetchCourses({
      instructorId: session.instructorId,
      isSuperadmin: session.isSuperadmin
    })
  ])
    .then(([s, t, c]) => {
      setSemesters(
        Array.isArray(s) ? s : []
      );

      setTitles(
        Array.isArray(t) ? t : []
      );

      setCourses(
        Array.isArray(c) ? c : []
      );
    })
    .catch(err => {
      console.error(
        'Course settings load failed:',
        err
      );

      setError(
        err?.message ||
          'Failed to load course settings.'
      );
    })
    .finally(() => setLoading(false));
}, [
  session.institutionId,
  session.instructorId,
  session.isSuperadmin
]);

  const courseName = selectedTitleId
    ? (titles.find(t => t.id === selectedTitleId)?.name || '')
    : manualName.trim();

  async function handleAdd(e) {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!semesterId) return setError('Select a semester.');
    const semester =
  semesters.find(
    s => s.id === semesterId
  );

if (!semester) {
  return setError(
    'The selected semester is not available for your institution.'
  );
}

const semesterWeekCount =
  Number(semester.week_count);

if (
  !Number.isInteger(semesterWeekCount) ||
  semesterWeekCount < 1
) {
  return setError(
    'The selected semester does not have a valid number of weeks. Correct it in General Settings before creating a course.'
  );
}
    if (!courseName) return setError('Enter or select a course name.');
    if (!classDays.length) return setError('Select at least one class day.');

    const duplicate = courses.find(c =>
      c.semester_id === semesterId &&
      (c.course_titles?.name || c.name || '').toLowerCase() === courseName.toLowerCase()
    );
    if (duplicate) return setError('That course already exists in this semester.');

    setSaving(true);
    try {
      let titleId = selectedTitleId;

      if (!titleId && addToTitles) {
        const existing = titles.find(t => t.name.toLowerCase() === courseName.toLowerCase());
        if (existing) {
          titleId = existing.id;
        } else {
          const result = await saveCourseTitle(courseName);
          titleId = result[0].id;
          setTitles(prev => [...prev, result[0]].sort((a, b) => a.name.localeCompare(b.name)));
        }
      }

      const result = await saveCourse({
      semester_id: semesterId,
      course_title_id: titleId || null,
      name: titleId ? null : courseName,
      class_days: [...classDays].sort(
  (a, b) => DAYS.indexOf(a) - DAYS.indexOf(b)
),
      class_times: {},
      pd_due_day: 'Sunday',
      pd_enabled: false,
      cq_enabled: false,
      cq_clarificatory_enabled: false,
      qi_enabled: false,
      voting_enabled: false,
      attendance_enabled: true,
      self_eval_enabled: false,
      instructor_id: session.instructorId
});

      const newCourse = result[0];
      const weeks = generateWeeks(
  newCourse.id,
  semester.start_date,
  [...classDays].sort(
    (a, b) =>
      DAYS.indexOf(a) -
      DAYS.indexOf(b)
  ),
  semester.week_count
);
      await saveWeeks(weeks);

      // Pre-populate weeks 17 and 18 with Self-Directed Study Week
      const allWeeks = await fetch(
        `${SUPABASE_URL}/rest/v1/course_weeks?course_id=eq.${newCourse.id}&week_number=gte.17&select=id`,
        {
          headers: {
            'apikey': SUPABASE_KEY,
            'Authorization': 'Bearer ' + SUPABASE_KEY
          }
        }
      ).then(r => r.json());

      if (allWeeks.length) {
        await fetch(
          `${SUPABASE_URL}/rest/v1/course_week_content`,
          {
            method: 'POST',
            headers: {
              'apikey': SUPABASE_KEY,
              'Authorization': 'Bearer ' + SUPABASE_KEY,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify(
              allWeeks.map(w => ({
                course_week_id: w.id,
                description: '<p>Self-Directed Study Week</p>'
              }))
            )
          }
        );
      }

            // Create readings folder in Drive
      const courseTitleName = titleId
        ? (titles.find(t => t.id === titleId)?.name || courseName)
        : courseName;
      await fetch(process.env.REACT_APP_API_URL, {
        method: 'POST',
        mode: 'no-cors',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'createCourseReadingsFolder',
          courseName: `${courseTitleName} - ${semester?.name || ''}`,
          courseId: newCourse.id
        })
      });

      // Enrol master student automatically
const masterResponse = await fetch(
  `${SUPABASE_URL}/rest/v1/students?is_master=eq.true&active=eq.true&select=id&limit=1`,
  {
    headers: {
      'apikey': SUPABASE_KEY,
      'Authorization': 'Bearer ' + SUPABASE_KEY
    }
  }
);

if (!masterResponse.ok) {
  const detail = await masterResponse.text();
  throw new Error(
    `Failed to find Master Student (${masterResponse.status}): ${detail}`
  );
}

const masterData = await masterResponse.json();

if (!masterData?.length) {
  throw new Error('No active Master Student account exists.');
}

const masterEnrolResponse = await fetch(
  `${SUPABASE_URL}/rest/v1/enrolments`,
  {
    method: 'POST',
    headers: {
      'apikey': SUPABASE_KEY,
      'Authorization': 'Bearer ' + SUPABASE_KEY,
      'Content-Type': 'application/json',
      'Prefer': 'return=representation'
    },
    body: JSON.stringify({
      student_id: masterData[0].id,
      course_id: newCourse.id,
      active: true
    })
  }
);

if (!masterEnrolResponse.ok) {
  const detail = await masterEnrolResponse.text();
  throw new Error(
    `Failed to enrol Master Student (${masterEnrolResponse.status}): ${detail}`
  );
}

      const updated = await fetchCourses(session);
      setCourses(updated);

      setSelectedTitleId('');
      setManualName('');
      setAddToTitles(false);
      setClassDays([]);
     setSuccess(
  `Course created with ${semester.week_count} weeks generated. Go to Course Management to configure it.`
);
    } catch (err) {
  console.error('Course creation failed:', err);

  setError(
    err?.message ||
    'Failed to create course.'
  );
} finally {
      setSaving(false);
    }
  }

  async function handleDelete(course) {
  if (course.instructor_id !== session.instructorId) return;

  const name =
    course.course_titles?.name ||
    course.name ||
    'this course';

  if (
    !window.confirm(
      `Delete "${name}"? All associated data will be lost.`
    )
  ) {
    return;
  }

  setError('');
  setSuccess('');

  try {
    await deleteCourse(course.id);

    setCourses(prev =>
      prev.filter(c => c.id !== course.id)
    );

    setSuccess('Course deleted.');
  } catch (err) {
    console.error('Course deletion failed:', err);

    setError(
      err?.message ||
      'Failed to delete course.'
    );
  }
}

  if (loading) return <div style={styles.muted}>Loading...</div>;

  return (
    <div>
      <h2 style={styles.subheading}>Courses</h2>
      <p style={styles.muted}>
        Create courses here, then configure their details in Course Management.
      </p>

      {error && <div style={styles.error}>{error}</div>}
      {success && <div style={styles.success}>{success}</div>}

{semesters.length === 0 && (
  <div style={styles.warning}>
    No semesters have been configured for your institution.
    Add a semester under General Settings → Semester before
    creating a course.
  </div>
)}

      <form onSubmit={handleAdd} style={styles.form}>
        <h3 style={styles.formHeading}>Add Course</h3>
        <div style={styles.row}>
          <div style={styles.field}>
            <label style={styles.label}>Semester</label>
            <select
  style={styles.input}
  value={semesterId}
  disabled={semesters.length === 0}
  onChange={e =>
    setSemesterId(e.target.value)
  }
>
              <option value="">Select a semester</option>
              {semesters.map(s => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
          </div>
          <div style={styles.field}>
            <label style={styles.label}>Course name (from list)</label>
            <select
              style={styles.input}
              value={selectedTitleId}
              onChange={e => {
                setSelectedTitleId(e.target.value);
                if (e.target.value) setManualName('');
              }}
            >
              <option value="">-- Select or type below --</option>
              {titles.map(t => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>
          </div>
          <div style={styles.field}>
            <label style={styles.label}>Or enter manually</label>
            <input
              style={styles.input}
              type="text"
              value={manualName}
              onChange={e => {
                setManualName(e.target.value);
                if (e.target.value) setSelectedTitleId('');
              }}
              placeholder="Course name"
            />
          </div>
          <div style={styles.field}>
            <label style={styles.label}>Class day(s)</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '4px' }}>
              {DAYS.map(d => (
                <label key={d} style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '14px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={classDays.includes(d)}
                    onChange={e => {
  setClassDays(prev => {
    const next = e.target.checked
      ? [...prev, d]
      : prev.filter(x => x !== d);

    return next.sort(
      (a, b) => DAYS.indexOf(a) - DAYS.indexOf(b)
    );
  });
}}
                  />
                  {d}
                </label>
              ))}
            </div>
          </div>
        </div>
        {!selectedTitleId && manualName.trim() && (
          <div style={styles.checkRow}>
            <label style={styles.checkLabel}>
              <input
                type="checkbox"
                checked={addToTitles}
                onChange={e => setAddToTitles(e.target.checked)}
                style={{ marginRight: '8px' }}
              />
              Add this name to the permanent course title list
            </label>
          </div>
        )}
        <button
  style={styles.btn}
  disabled={
    saving ||
    semesters.length === 0 ||
    !semesterId
  }
>
          {saving ? 'Creating...' : 'Create Course'}
        </button>
      </form>

      {courses.length === 0 && (
        <div style={styles.muted}>No courses created yet.</div>
      )}

      {courses.length > 0 && (
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>Course</th>
              <th style={styles.th}>Semester</th>
              <th style={styles.th}>Class Days</th>
              <th style={styles.th}></th>
            </tr>
          </thead>
          <tbody>
            {courses.map(course => (
              <tr key={course.id}>
                <td style={styles.td}>
                  {course.course_titles?.name || course.name || <em style={{ color: '#66788a' }}>Untitled</em>}
                </td>
                <td style={styles.td}>{course.semesters?.name}</td>
                <td style={styles.td}>
  {Array.isArray(course.class_days)
    ? [...course.class_days]
        .sort(
          (a, b) =>
            DAYS.indexOf(a) - DAYS.indexOf(b)
        )
        .join(', ')
    : (course.class_days || course.class_day || '')}
</td>
                <td style={styles.td}>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                      style={styles.configBtn}
                      onClick={() => onConfigure && onConfigure(course.id)}
                    >
                      Configure →
                    </button>
                    {course.instructor_id === session.instructorId && (
  <button
    style={styles.dangerBtn}
    onClick={() => handleDelete(course)}
  >
    Delete
  </button>
)}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

const styles = {
  subheading: { margin: '0 0 8px', fontSize: '18px', color: '#1f2933' },
  muted: { color: '#66788a', fontSize: '14px', margin: '0 0 20px' },
  error: {
    background: '#faeaea', border: '1px solid #e8bcbc', borderRadius: '8px',
    padding: '10px 14px', color: '#a12a2a', fontSize: '14px', marginBottom: '16px'
  },
  success: {
    background: '#e7f5ed', border: '1px solid #b9dfc9', borderRadius: '8px',
    padding: '10px 14px', color: '#216e45', fontSize: '14px', marginBottom: '16px'
  },
  form: {
    border: '1px solid #d7dee5', borderRadius: '10px', padding: '20px',
    background: '#f7f9fa', marginBottom: '24px'
  },
  formHeading: { margin: '0 0 16px', fontSize: '16px', color: '#1f2933' },
  row: { display: 'flex', gap: '16px', flexWrap: 'wrap', marginBottom: '12px' },
  field: { flex: 1, minWidth: '160px' },
  label: { display: 'block', fontWeight: '700', fontSize: '13px', marginBottom: '6px', color: '#1f2933' },
  input: {
    width: '100%', border: '1px solid #d7dee5', borderRadius: '8px',
    padding: '9px 11px', fontSize: '14px', boxSizing: 'border-box', background: 'white'
  },
  checkRow: { marginBottom: '14px' },
  checkLabel: { fontSize: '14px', color: '#1f2933', display: 'flex', alignItems: 'center', cursor: 'pointer' },
  btn: {
    background: '#215b7d', color: 'white', border: 'none', borderRadius: '8px',
    padding: '9px 16px', fontSize: '14px', fontWeight: '600', cursor: 'pointer'
  },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: {
    textAlign: 'left', padding: '9px 11px', fontSize: '13px',
    color: '#42515f', background: '#f0f4f7', borderBottom: '1px solid #d7dee5'
  },
  td: { padding: '10px 11px', borderBottom: '1px solid #d7dee5', fontSize: '14px' },
  configBtn: {
    background: 'transparent', color: '#215b7d', border: '1px solid #b0cfe0',
    borderRadius: '6px', padding: '5px 10px', fontSize: '13px', cursor: 'pointer'
  },
  dangerBtn: {
    background: 'transparent', color: '#a12a2a', border: '1px solid #e2b8b8',
    borderRadius: '6px', padding: '5px 10px', fontSize: '13px', cursor: 'pointer'
  },
  warning: {
  background: '#fff8e5',
  border: '1px solid #ead59a',
  borderRadius: '8px',
  padding: '10px 14px',
  color: '#725500',
  fontSize: '14px',
  marginBottom: '16px',
  lineHeight: '1.5'
},
};